import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOrgProject } from '../contexts/OrgProjectContext';
import { apiFetch } from '../apiClient';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  blue:   '#3B82F6',
  green:  '#10B981',
  amber:  '#F59E0B',
  red:    '#EF4444',
  violet: '#8B5CF6',
  cyan:   '#06B6D4',
  navy:   '#0F172A',
  navyMid:'#1E293B',
  navyLight:'#334155',
  slate:  '#64748B',
  slateLight:'#94A3B8',
  border: '#E2E8F0',
  bg:     '#F8FAFC',
  white:  '#FFFFFF',
  text:   '#1E293B',
  textMid:'#475569',
};

const DECISION_COLORS = {
  Rehost:     '#06B6D4',
  Replatform: '#8B5CF6',
  Refactor:   '#3B82F6',
  Repurchase: '#F59E0B',
  Relocate:   '#64748B',
  Retain:     '#94A3B8',
  Retire:     '#EF4444',
  // Legacy compat
  New:        '#10B981',
  Replace:    '#F59E0B',
};

const SEVERITY_COLORS = { High: C.red, Medium: C.amber, Low: C.green };

const shadow = '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)';
const shadowMd = '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.06)';

// ─── Utility helpers ──────────────────────────────────────────────────────────
const fmt = (n, dec = 0) =>
  n == null ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtK = n => n == null ? '—' : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
const fmtUSD = n => n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const pct = n => n == null ? '—' : `${Number(n).toFixed(1)}%`;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Format cost: show cents when < $1, dollars when >= $1
const fmtCost = (usd) => {
  if (usd == null || usd === 0) return '—';
  if (usd < 0.01) return `$${usd.toFixed(5)}`;
  if (usd < 1)    return `${(usd * 100).toFixed(1)}¢`;
  if (usd < 10)   return `$${usd.toFixed(2)}`;
  return `$${usd.toFixed(2)}`;
};
function Tooltip({ tip }) {
  if (!tip) return null;
  return (
    <div style={{
      position: 'fixed',
      left: tip.x + 14, top: tip.y - 10,
      background: C.navyMid, color: C.white,
      padding: '7px 11px', borderRadius: 8,
      fontSize: 12, fontWeight: 500,
      boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
      pointerEvents: 'none', zIndex: 9999,
      whiteSpace: 'nowrap', lineHeight: 1.5,
      border: `1px solid ${C.navyLight}`,
      maxWidth: 220,
    }}>
      {tip.lines.map((l, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {l.color && <div style={{ width: 8, height: 8, borderRadius: 2, background: l.color, flexShrink: 0 }} />}
          <span style={{ color: l.dim ? C.slateLight : C.white, fontWeight: l.bold ? 700 : 500 }}>{l.text}</span>
        </div>
      ))}
    </div>
  );
}

function useTooltip() {
  const [tip, setTip] = React.useState(null);
  const show = (e, lines) => setTip({ x: e.clientX, y: e.clientY, lines });
  const move = (e) => setTip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
  const hide = () => setTip(null);
  return { tip, show, move, hide };
}

// ─── Base Card ────────────────────────────────────────────────────────────────
function Card({ children, style }) {
  return (
    <div style={{
      background: C.white, borderRadius: 12, boxShadow: shadow,
      border: `1px solid ${C.border}`, padding: '16px 20px',
      overflow: 'visible',
      ...style
    }}>
      {children}
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ icon, title, subtitle }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>{title}</h2>
      </div>
      {subtitle && <p style={{ margin: '4px 0 0 32px', fontSize: 13, color: C.textMid }}>{subtitle}</p>}
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color, icon }) {
  return (
    <Card style={{ flex: 1, minWidth: 140 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.slate, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: color || C.text, lineHeight: 1 }}>{value}</div>
          {sub && <div style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>{sub}</div>}
        </div>
        {icon && <span style={{ fontSize: 24, opacity: 0.7 }}>{icon}</span>}
      </div>
    </Card>
  );
}

// ─── Donut Chart (pure SVG) ───────────────────────────────────────────────────
function DonutChart({ data, size = 160, thickness = 32, label, sublabel }) {
  const { tip, show, move, hide } = useTooltip();
  const r = (size - thickness) / 2;
  const cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  if (!total) return <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.slate, fontSize: 12 }}>No data</div>;

  let cumulative = 0;
  const slices = [];
  const labels = [];
  data.forEach((d, i) => {
    const frac = d.value / total;
    const dash = frac * circ;
    const gap = circ - dash;
    const dashOffset = circ / 4 - (cumulative / total) * circ;
    // Label position: midpoint angle of this slice
    const midFrac = (cumulative + d.value / 2) / total;
    const midAngle = -Math.PI / 2 + midFrac * 2 * Math.PI;
    const labelR = r; // Place labels on the arc itself (inside the slice)
    const lx = cx + labelR * Math.cos(midAngle);
    const ly = cy + labelR * Math.sin(midAngle);
    cumulative += d.value;

    slices.push(
      <circle key={i} cx={cx} cy={cy} r={r}
        fill="none" stroke={d.color} strokeWidth={thickness}
        strokeDasharray={`${dash} ${gap}`}
        strokeDashoffset={dashOffset}
        style={{ cursor: 'pointer', transition: 'stroke-width 0.15s' }}
        onMouseEnter={e => show(e, [
          { text: d.label, bold: true, color: d.color },
          { text: `${fmt(d.value)} apps  (${((d.value / total) * 100).toFixed(1)}%)`, dim: true },
        ])}
        onMouseMove={move}
        onMouseLeave={hide}
      />
    );
    // Only show label if slice is big enough (>8%)
    if (frac > 0.08) {
      labels.push(
        <text key={`lbl-${i}`} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
          fontSize={9} fontWeight={700} fill={C.white}
          stroke={C.navyMid} strokeWidth={2} paintOrder="stroke">
          {d.label}
        </text>
      );
    }
  });

  return (
    <>
      <Tooltip tip={tip} />
      <svg width={size} height={size} style={{ display: 'block', margin: '0 auto' }}>
        <g>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.border} strokeWidth={thickness} />
          {slices}
          {labels}
          {label && <text x={cx} y={cy - 6} textAnchor="middle" fontSize={18} fontWeight={800} fill={C.text}>{label}</text>}
          {sublabel && <text x={cx} y={cy + 14} textAnchor="middle" fontSize={11} fill={C.slate}>{sublabel}</text>}
        </g>
      </svg>
    </>
  );
}

// ─── Horizontal Bar Chart ─────────────────────────────────────────────────────
function HBarChart({ data, color, height = 220, showValues = true, format }) {
  const { tip, show, move, hide } = useTooltip();
  if (!data || !data.length) return <div style={{ color: C.slate, fontSize: 12 }}>No data</div>;
  const max = Math.max(...data.map(d => d.value));
  const barH = Math.max(16, Math.floor((height - data.length * 6) / data.length));
  return (
    <div style={{ width: '100%' }}>
      <Tooltip tip={tip} />
      {data.map((d, i) => (
        <div key={i} style={{ marginBottom: 6 }}
          onMouseEnter={e => show(e, [
            { text: d.label, bold: true, color: d.color || color || C.blue },
            { text: format ? format(d.value) : fmt(d.value), dim: true },
          ])}
          onMouseMove={move}
          onMouseLeave={hide}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
            <span style={{ fontSize: 11, color: C.textMid, fontWeight: 500 }}>{d.label}</span>
            {showValues && <span style={{ fontSize: 11, color: C.text, fontWeight: 700 }}>
              {format ? format(d.value) : fmt(d.value)}
            </span>}
          </div>
          <div style={{ background: C.border, borderRadius: 4, height: barH, overflow: 'hidden', cursor: 'pointer' }}>
            <div style={{
              height: '100%', borderRadius: 4,
              width: `${max > 0 ? (d.value / max) * 100 : 0}%`,
              background: d.color || color || C.blue,
              transition: 'width 0.5s ease'
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Gauge Chart (pure SVG) ───────────────────────────────────────────────────
function GaugeChart({ value, max = 100, color, label, size = 120 }) {
  const pctVal = clamp((value || 0) / max, 0, 1);
  const cx = size / 2, cy = size * 0.52;
  const r = size * 0.36;
  const arcLen = Math.PI * r;
  const filled = pctVal * arcLen;
  const gap = arcLen - filled;
  const x1 = cx - r, y1 = cy;
  const x2 = cx + r, y2 = cy;
  const needleAngle = Math.PI + pctVal * Math.PI;
  const nx = cx + (r * 0.72) * Math.cos(needleAngle);
  const ny = cy + (r * 0.72) * Math.sin(needleAngle);

  return (
    <svg width={size} height={size * 0.82} style={{ display: 'block', margin: '0 auto' }}>
      {/* Track */}
      <path d={`M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`}
        fill="none" stroke={C.border} strokeWidth={10} strokeLinecap="round" />
      {/* Fill */}
      <path d={`M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`}
        fill="none" stroke={color || C.blue} strokeWidth={10} strokeLinecap="round"
        strokeDasharray={`${filled} ${gap}`}
        strokeDashoffset={0}
      />
      {/* Needle */}
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={C.navyMid} strokeWidth={2} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={4} fill={C.navyMid} />
      {/* Value */}
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize={15} fontWeight={800} fill={C.text}>
        {value != null ? `${Math.round(value)}%` : '—'}
      </text>
      {label && <text x={cx} y={cy + 16} textAnchor="middle" fontSize={10} fill={C.slate}>{label}</text>}
    </svg>
  );
}

// ─── Grouped Bar Chart (pure SVG) ─────────────────────────────────────────────
function GroupedBarChart({ data, groups, colors, height = 200 }) {
  const { tip, show, move, hide } = useTooltip();
  if (!data || !data.length) return <div style={{ color: C.slate, fontSize: 12 }}>No data</div>;
  const allVals = data.flatMap(d => d.values);
  const max = Math.max(...allVals, 1);
  const svgW = 420, svgH = height;
  const padL = 50, padR = 10, padT = 10, padB = 40;
  const chartW = svgW - padL - padR;
  const chartH = svgH - padT - padB;
  const groupW = chartW / data.length;
  const barW = Math.min(18, (groupW - 8) / groups.length);
  const gap = 3;
  const ticks = Array.from({ length: 5 }, (_, i) => (max * i) / 4);
  return (
    <>
      <Tooltip tip={tip} />
      <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block' }}>
        {ticks.map((t, i) => {
          const y = padT + chartH - (t / max) * chartH;
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={padL + chartW} y2={y} stroke={C.border} strokeWidth={1} />
              <text x={padL - 4} y={y + 4} textAnchor="end" fontSize={9} fill={C.slate}>
                {t >= 1000 ? `${(t/1000).toFixed(0)}k` : Math.round(t)}
              </text>
            </g>
          );
        })}
        {data.map((d, gi) => {
          const gx = padL + gi * groupW + groupW / 2 - (groups.length * (barW + gap)) / 2;
          return (
            <g key={gi}>
              {d.values.map((v, bi) => {
                const bh = (v / max) * chartH;
                const bx = gx + bi * (barW + gap);
                const by = padT + chartH - bh;
                return (
                  <rect key={bi} x={bx} y={by} width={barW} height={bh}
                    fill={colors[bi]} rx={2} opacity={0.9}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={e => show(e, [
                      { text: d.label, bold: true },
                      { text: `${groups[bi]}: ${v >= 1000 ? `$${(v/1000).toFixed(1)}k` : fmt(v)}`, color: colors[bi] },
                    ])}
                    onMouseMove={move}
                    onMouseLeave={hide}
                  />
                );
              })}
              <text x={padL + gi * groupW + groupW / 2} y={svgH - padB + 14}
                textAnchor="middle" fontSize={9} fill={C.textMid}>
                {d.label.length > 10 ? d.label.slice(0, 9) + '…' : d.label}
              </text>
            </g>
          );
        })}
        {groups.map((g, i) => (
          <g key={i} transform={`translate(${padL + i * 90}, ${svgH - 10})`}>
            <rect x={0} y={-8} width={10} height={10} fill={colors[i]} rx={2} />
            <text x={14} y={0} fontSize={9} fill={C.textMid}>{g}</text>
          </g>
        ))}
      </svg>
    </>
  );
}

// ─── Waterfall Chart (pure SVG) ───────────────────────────────────────────────
function WaterfallChart({ items, height = 200 }) {
  const { tip, show, move, hide } = useTooltip();
  if (!items || !items.length) return <div style={{ color: C.slate, fontSize: 12 }}>No data</div>;
  const svgW = 420, svgH = height;
  const padL = 55, padR = 10, padT = 10, padB = 50;
  const chartW = svgW - padL - padR;
  const chartH = svgH - padT - padB;
  const max = Math.max(...items.map(d => d.total || d.value || 0), 1);
  const barW = Math.max(18, chartW / items.length - 8);
  return (
    <>
      <Tooltip tip={tip} />
      <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block' }}>
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
          const y = padT + chartH * (1 - f);
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={padL + chartW} y2={y} stroke={C.border} strokeWidth={1} />
              <text x={padL - 4} y={y + 4} textAnchor="end" fontSize={9} fill={C.slate}>
                {fmtK(Math.round(max * f))}
              </text>
            </g>
          );
        })}
        {items.map((d, i) => {
          const val = d.value || 0;
          const tot = d.total || val;
          const base = tot - val;
          const bh = (val / max) * chartH;
          const baseH = (base / max) * chartH;
          const bx = padL + i * (chartW / items.length) + (chartW / items.length - barW) / 2;
          const by = padT + chartH - baseH - bh;
          const color = d.positive ? C.green : C.red;
          return (
            <g key={i}>
              {base > 0 && <rect x={bx} y={padT + chartH - baseH} width={barW} height={baseH} fill="transparent" />}
              <rect x={bx} y={by} width={barW} height={Math.max(bh, 2)} fill={color} rx={2} opacity={0.85}
                style={{ cursor: 'pointer' }}
                onMouseEnter={e => show(e, [
                  { text: d.label, bold: true, color },
                  { text: `$${Number(val).toLocaleString()}`, dim: true },
                ])}
                onMouseMove={move}
                onMouseLeave={hide}
              />
              <text x={bx + barW / 2} y={svgH - padB + 14} textAnchor="middle" fontSize={8} fill={C.textMid}>
                {(d.label || '').length > 8 ? d.label.slice(0, 7) + '…' : d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </>
  );
}

// ─── Line Chart (pure SVG) ────────────────────────────────────────────────────
function LineChart({ series, height = 160, yMax }) {
  // series: [{label, color, points: [number]}]
  if (!series || !series.length || !series[0].points.length)
    return <div style={{ color: C.slate, fontSize: 12 }}>No trend data yet</div>;
  const svgW = 420, svgH = height;
  const padL = 36, padR = 10, padT = 10, padB = 24;
  const chartW = svgW - padL - padR;
  const chartH = svgH - padT - padB;
  const allPts = series.flatMap(s => s.points);
  const max = yMax || Math.max(...allPts, 1);
  const n = series[0].points.length;
  const xStep = n > 1 ? chartW / (n - 1) : chartW;

  const toPath = pts =>
    pts.map((v, i) => {
      const x = padL + i * xStep;
      const y = padT + chartH - (v / max) * chartH;
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

  return (
    <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block' }}>
      {[0, 0.5, 1].map((f, i) => {
        const y = padT + chartH * (1 - f);
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={padL + chartW} y2={y} stroke={C.border} strokeWidth={1} />
            <text x={padL - 4} y={y + 4} textAnchor="end" fontSize={9} fill={C.slate}>{Math.round(max * f)}</text>
          </g>
        );
      })}
      {series.map((s, i) => (
        <path key={i} d={toPath(s.points)} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" />
      ))}
      {series.map((s, i) => (
        <g key={i} transform={`translate(${padL + i * 80}, ${svgH - 6})`}>
          <rect x={0} y={-8} width={10} height={3} fill={s.color} rx={1} />
          <text x={14} y={0} fontSize={9} fill={C.textMid}>{s.label}</text>
        </g>
      ))}
    </svg>
  );
}

// ─── Decision Badge ───────────────────────────────────────────────────────────
function Badge({ label }) {
  const color = DECISION_COLORS[label] || C.slate;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 99,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
      background: `${color}20`, color, border: `1px solid ${color}40`
    }}>{label}</span>
  );
}

// ─── Risk Score Bar ───────────────────────────────────────────────────────────
function RiskBar({ score, reason }) {
  const [tip, setTip] = React.useState(null);
  const v = clamp((score || 0) * 10, 0, 100);  // Convert 0-10 score to 0-100%
  const color = v >= 70 ? C.red : v >= 40 ? C.amber : C.green;
  const label = v >= 70 ? 'High' : v >= 40 ? 'Medium' : 'Low';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative', cursor: reason ? 'pointer' : 'default' }}
      onMouseEnter={e => reason && setTip({ x: e.clientX, y: e.clientY })}
      onMouseMove={e => tip && setTip({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setTip(null)}>
      {tip && reason && (
        <div style={{
          position: 'fixed', left: tip.x + 12, top: tip.y - 10,
          background: C.navyMid, color: C.white, padding: '8px 12px', borderRadius: 8,
          fontSize: 11, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          pointerEvents: 'none', zIndex: 9999, maxWidth: 260, lineHeight: 1.4,
          border: `1px solid ${C.navyLight}`,
        }}>
          {reason}
        </div>
      )}
      <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${v}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color, minWidth: 44 }}>{v}% {label}</span>
    </div>
  );
}

// ─── Stacked Bar Chart ────────────────────────────────────────────────────────
function StackedBarChart({ data, keys, colors, height = 180 }) {
  // data: [{label, values: {key: number}}]
  const [tip, setTip] = React.useState(null);
  const COMPLEXITY_TIPS = {
    'Low': 'Quick win — minimal dependencies, straightforward migration with low risk',
    'Medium': 'Moderate effort — some refactoring needed, manageable within a sprint',
    'High': 'Significant investment — complex dependencies, requires careful planning',
    'Very High': 'Major undertaking — deep coupling, legacy patterns, needs phased approach',
  };
  if (!data || !data.length) return <div style={{ color: C.slate, fontSize: 12 }}>No data</div>;
  const totals = data.map(d => keys.reduce((s, k) => s + (d.values[k] || 0), 0));
  const max = Math.max(...totals, 1);
  const barH = Math.max(18, Math.floor((height - data.length * 8) / data.length));

  return (
    <div style={{ position: 'relative' }}>
      {tip && (
        <div style={{
          position: 'fixed', left: tip.x + 12, top: tip.y - 10,
          background: C.navyMid, color: C.white, padding: '8px 12px', borderRadius: 8,
          fontSize: 11, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          pointerEvents: 'none', zIndex: 9999, maxWidth: 260, lineHeight: 1.4,
          border: `1px solid ${C.navyLight}`, whiteSpace: 'normal',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 3 }}>{tip.key}: {tip.count} app{tip.count !== 1 ? 's' : ''}</div>
          <div style={{ color: C.slateLight }}>{tip.desc}</div>
        </div>
      )}
      {data.map((d, i) => {
        const total = totals[i];
        return (
          <div key={i} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <span style={{ fontSize: 11, color: C.textMid, fontWeight: 500 }}>{d.label}</span>
              <span style={{ fontSize: 11, color: C.text, fontWeight: 700 }}>{total}</span>
            </div>
            <div style={{ display: 'flex', height: barH, borderRadius: 4, overflow: 'hidden', background: C.border }}>
              {keys.map((k, ki) => {
                const v = d.values[k] || 0;
                const w = total > 0 ? (v / total) * 100 : 0;
                return w > 0 ? (
                  <div key={ki} style={{
                    width: `${w}%`, background: colors[ki], height: '100%',
                    transition: 'width 0.4s ease', cursor: 'pointer',
                  }}
                    onMouseEnter={e => setTip({ x: e.clientX, y: e.clientY, key: k, count: v, desc: COMPLEXITY_TIPS[k] || k })}
                    onMouseMove={e => setTip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                    onMouseLeave={() => setTip(null)}
                  />
                ) : null;
              })}
            </div>
          </div>
        );
      })}
      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
        {keys.map((k, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: colors[i] }} />
            <span style={{ fontSize: 10, color: C.textMid }}>{k}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Funnel Chart (pure SVG) ──────────────────────────────────────────────────
function FunnelChart({ stages, height = 220 }) {
  if (!stages || !stages.length) return <div style={{ color: C.slate, fontSize: 12 }}>No data</div>;
  const max = stages[0].value || 1;
  const svgW = 380, svgH = height;
  const stageH = svgH / stages.length;
  const maxW = svgW * 0.7;
  const cx = svgW / 2;

  return (
    <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block' }}>
      {stages.map((s, i) => {
        const w = maxW * (s.value / max);
        const x = cx - w / 2;
        const y = i * stageH + 2;
        const h = stageH - 4;
        const opacity = 0.9 - i * 0.08;
        return (
          <g key={i}>
            <rect x={x} y={y} width={w} height={h} rx={4}
              fill={s.color || C.blue} opacity={Math.max(opacity, 0.4)} />
            <text x={cx} y={y + h / 2 + 5} textAnchor="middle" fontSize={11} fontWeight={600} fill={C.white}>
              {s.label}
            </text>
            <text x={cx + w / 2 + 8} y={y + h / 2 + 5} textAnchor="start" fontSize={10} fill={C.textMid}>
              {fmt(s.value)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Scale Projection Card ────────────────────────────────────────────────────
function ScaleCard({ apps, cost, savings, weeks }) {
  return (
    <Card style={{ flex: 1, minWidth: 120, textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: C.blue }}>{fmt(apps)}</div>
      <div style={{ fontSize: 10, color: C.slate, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Apps</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{fmtUSD(cost)}</div>
      <div style={{ fontSize: 10, color: C.textMid, marginBottom: 4 }}>AI Cost</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>{fmtUSD(savings)}</div>
      <div style={{ fontSize: 10, color: C.textMid, marginBottom: 4 }}>Savings</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.amber }}>{weeks != null ? `${Number(weeks).toFixed(0)}w` : '—'}</div>
      <div style={{ fontSize: 10, color: C.textMid }}>Weeks Saved</div>
    </Card>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ orgName, projectName, isOrgLevel }) {
  const heading = isOrgLevel
    ? `No pipeline runs yet for ${orgName || 'this organisation'}`
    : projectName
      ? `No pipeline runs yet for ${projectName}`
      : 'No data yet — run the pipeline';

  const description = isOrgLevel
    ? `Once you run a pipeline for any project within ${orgName || 'this organisation'}, aggregate statistics will appear here.`
    : projectName
      ? `Run a pipeline for ${projectName} to see its modernisation metrics and portfolio intelligence.`
      : 'Once you run your first pipeline, your portfolio intelligence will appear here.';

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: 400, gap: 16
    }}>
      <div style={{ fontSize: 64 }}>🚀</div>
      <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: C.text, textAlign: 'center' }}>{heading}</h2>
      <p style={{ margin: 0, color: C.textMid, fontSize: 15, textAlign: 'center', maxWidth: 480 }}>
        {description}
      </p>
    </div>
  );
}

// ─── Tab Button ───────────────────────────────────────────────────────────────
function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
      fontSize: 13, fontWeight: 600,
      background: active ? C.blue : 'transparent',
      color: active ? C.white : C.slateLight,
      transition: 'all 0.2s',
    }}>{children}</button>
  );
}

// ─── Pre/Post Comparison Charts ───────────────────────────────────────────────

function PrePostBarChart({ apps }) {
  // Grouped bar: Before (red) vs After (green) for Security Issues and Coverage
  const svgW = 380, svgH = 160;
  const padL = 60, padR = 20, padT = 16, padB = 32;
  const chartW = svgW - padL - padR;
  const chartH = svgH - padT - padB;

  const metrics = apps.map(a => ({
    name: a.name.length > 16 ? a.name.slice(0, 14) + '…' : a.name,
    preIssues: a.pre_issues || 0,
    postIssues: a.post_issues || 0,
    preCov: a.pre_coverage || 0,
    postCov: a.post_coverage || 0,
  }));

  const groups = [
    { label: 'Security Issues', pre: metrics.reduce((s, m) => s + m.preIssues, 0), post: metrics.reduce((s, m) => s + m.postIssues, 0) },
    { label: 'Test Coverage %', pre: metrics.length ? metrics.reduce((s, m) => s + m.preCov, 0) / metrics.length : 0, post: metrics.length ? metrics.reduce((s, m) => s + m.postCov, 0) / metrics.length : 0 },
  ];
  const max = Math.max(...groups.flatMap(g => [g.pre, g.post]), 1);
  const groupW = chartW / groups.length;
  const barW = 24;
  const gap = 6;

  return (
    <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block' }}>
      {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
        const y = padT + chartH * (1 - f);
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={padL + chartW} y2={y} stroke={C.border} strokeWidth={1} />
            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize={8} fill={C.slate}>{Math.round(max * f)}</text>
          </g>
        );
      })}
      {groups.map((g, gi) => {
        const gx = padL + gi * groupW + groupW / 2;
        const preH = (g.pre / max) * chartH;
        const postH = (g.post / max) * chartH;
        return (
          <g key={gi}>
            <rect x={gx - barW - gap / 2} y={padT + chartH - preH} width={barW} height={Math.max(preH, 2)}
              fill={C.red} rx={3} opacity={0.8} />
            <text x={gx - barW / 2 - gap / 2} y={padT + chartH - preH - 4}
              textAnchor="middle" fontSize={9} fontWeight={700} fill={C.red}>
              {g.pre % 1 === 0 ? g.pre : g.pre.toFixed(1)}
            </text>
            <rect x={gx + gap / 2} y={padT + chartH - postH} width={barW} height={Math.max(postH, 2)}
              fill={C.green} rx={3} opacity={0.8} />
            <text x={gx + barW / 2 + gap / 2} y={padT + chartH - postH - 4}
              textAnchor="middle" fontSize={9} fontWeight={700} fill={C.green}>
              {g.post % 1 === 0 ? g.post : g.post.toFixed(1)}
            </text>
            <text x={gx} y={svgH - padB + 12} textAnchor="middle" fontSize={9} fill={C.textMid}>{g.label}</text>
          </g>
        );
      })}
      {/* Legend */}
      <rect x={padL} y={svgH - 10} width={8} height={8} fill={C.red} rx={2} opacity={0.8} />
      <text x={padL + 11} y={svgH - 3} fontSize={8} fill={C.textMid}>Before</text>
      <rect x={padL + 50} y={svgH - 10} width={8} height={8} fill={C.green} rx={2} opacity={0.8} />
      <text x={padL + 61} y={svgH - 3} fontSize={8} fill={C.textMid}>After</text>
    </svg>
  );
}

function PrePostTestChart({ apps }) {
  // Horizontal bar showing tests added per app — compact
  const barH = 18;
  const rowH = barH + 8;
  const svgW = 380, svgH = apps.length * rowH + 10;
  const padL = 90, padR = 60, padT = 4;
  const chartW = svgW - padL - padR;
  const max = Math.max(...apps.map(a => a.post_test_count || 0), 1);

  return (
    <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block' }}>
      {apps.map((app, i) => {
        const y = padT + i * rowH;
        const w = ((app.post_test_count || 0) / max) * chartW;
        const name = app.name.length > 12 ? app.name.slice(0, 10) + '…' : app.name;
        return (
          <g key={i}>
            <text x={padL - 6} y={y + barH / 2 + 3} textAnchor="end" fontSize={9} fill={C.textMid}>{name}</text>
            <rect x={padL} y={y} width={chartW} height={barH} fill={C.border} rx={3} opacity={0.4} />
            <rect x={padL} y={y} width={Math.max(w, 4)} height={barH} fill={C.green} rx={3} opacity={0.85} />
            <text x={padL + w + 5} y={y + barH / 2 + 3} fontSize={9} fontWeight={700} fill={C.green}>
              {app.post_test_count || 0}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function CollapsibleAppCards({ apps }) {
  const [expanded, setExpanded] = React.useState(true);
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 0', marginBottom: expanded ? 12 : 0 }}>
        <span style={{ fontSize: 14, transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Per-Application Detail</span>
        <span style={{ fontSize: 10, color: C.slate, background: C.bg, padding: '2px 8px', borderRadius: 4 }}>{apps.length} app{apps.length !== 1 ? 's' : ''}</span>
      </div>
      {expanded && apps.map((app, i) => (
        <Card key={i} style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'stretch' }}>
            <div style={{ flex: 1, padding: '14px 16px', background: `${C.red}06`, borderRight: `2px dashed ${C.border}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.red, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                ⚠️ Before (Legacy)
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>{app.name}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <MetricPill label="Language" value={(!app.pre_language || app.pre_language.toLowerCase() === 'unknown') ? 'Legacy' : app.pre_language} color={C.red} />
                <MetricPill label="Framework" value={`${(!app.pre_framework || app.pre_framework.toLowerCase() === 'unknown') ? 'Legacy' : app.pre_framework}${app.pre_eol ? ' ⚠️ EOL' : ''}`} color={C.red} />
                <MetricPill label="Architecture" value={app.pre_arch || 'Other'} color={C.red} />
                <MetricPill label="Issues" value={`${app.pre_issues} found`} color={C.red} />
                <MetricPill label="Test Coverage" value="0%" color={C.red} />
                <MetricPill label="Automated Tests" value="0" color={C.red} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, background: C.bg }}>
              <svg width="32" height="32" viewBox="0 0 32 32">
                <path d="M6 16 L22 16 M18 10 L24 16 L18 22" fill="none" stroke={C.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div style={{ flex: 1, padding: '14px 16px', background: `${C.green}06` }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.green, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                ✅ After (AI-Modernized)
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>
                {app.name} <Badge label={app.decision} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <MetricPill label="Language" value={(!app.post_language || app.post_language.toLowerCase() === 'unknown') ? '—' : app.post_language} color={C.green} />
                <MetricPill label="Framework" value={(!app.post_framework || app.post_framework.toLowerCase() === 'unknown') ? '—' : app.post_framework} color={C.green} />
                <MetricPill label="Architecture" value={app.post_arch || 'Modern'} color={C.green} />
                <MetricPill label="Issues" value={`${app.post_issues} remaining`} color={C.green} />
                <MetricPill label="Test Coverage" value={`${app.post_coverage}%`} color={C.green} />
                <MetricPill label="Automated Tests" value={`${app.post_test_count}`} color={C.green} />
              </div>
            </div>
          </div>
          <div style={{ padding: '10px 16px', background: C.bg, borderTop: `1px solid ${C.border}`, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            <ImprovementBar label="Issues Fixed" pct={app.issues_fixed_pct} detail={`${app.pre_issues} → ${app.post_issues}`} />
            <ImprovementBar label="Coverage Gained" pct={Math.min(app.post_coverage, 100)} detail={`0% → ${app.post_coverage}%`} />
            <ImprovementBar label="Tests Added" pct={Math.min(100, app.post_test_count > 0 ? 100 : 0)} detail={`0 → ${app.post_test_count} tests`} />
          </div>
        </Card>
      ))}
    </div>
  );
}

function AppNameTip({ app }) {
  const [tip, setTip] = React.useState(null);
  const domain = app.domain || '';
  const lang = (app.language && app.language.toLowerCase() !== 'unknown') ? app.language : '';
  const fw = (app.framework && app.framework.toLowerCase() !== 'unknown') ? app.framework : '';
  const loc = app.loc || 0;
  const arch = (app.architecture_pattern && app.architecture_pattern.toLowerCase() !== 'unknown')
    ? app.architecture_pattern.split('(')[0].trim() : '';
  const brief = [
    lang && fw ? `${lang} / ${fw}` : lang || fw || 'Legacy stack',
    loc ? `${loc.toLocaleString()} LOC` : '',
    arch || '',
    domain || '',
  ].filter(Boolean).join(' · ');

  return (
    <span style={{ cursor: 'pointer' }}
      onMouseEnter={e => setTip({ x: e.clientX, y: e.clientY })}
      onMouseMove={e => tip && setTip({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setTip(null)}>
      {app.name}
      {tip && (
        <div style={{
          position: 'fixed', left: tip.x + 12, top: tip.y - 10,
          background: C.navyMid, color: C.white, padding: '8px 12px', borderRadius: 8,
          fontSize: 11, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          pointerEvents: 'none', zIndex: 9999, maxWidth: 280, lineHeight: 1.5,
          border: `1px solid ${C.navyLight}`, whiteSpace: 'normal',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{app.name}</div>
          <div style={{ color: C.slateLight }}>{brief}</div>
        </div>
      )}
    </span>
  );
}

function MetricPill({ label, value, color }) {
  return (
    <div style={{ background: `${color}08`, border: `1px solid ${color}20`, borderRadius: 6, padding: '4px 8px' }}>
      <div style={{ fontSize: 9, color: C.slate, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.text, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
    </div>
  );
}

function ImprovementBar({ label, pct, detail }) {
  const v = clamp(pct || 0, 0, 100);
  const color = v >= 80 ? C.green : v >= 40 ? C.blue : C.amber;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: C.textMid }}>{label}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color }}>{v}%</span>
      </div>
      <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${v}%`, background: color, borderRadius: 4, transition: 'width 0.6s ease' }} />
      </div>
      <div style={{ fontSize: 9, color: C.slate, marginTop: 2 }}>{detail}</div>
    </div>
  );
}

function CriticalFindingsCard({ appsWithFindings, maxFindings, prePost }) {
  const [hoverTip, setHoverTip] = React.useState(null);
  const prePostMap = {};
  (prePost || []).forEach(p => { prePostMap[p.name] = p; });

  return (
    <Card style={{ marginBottom: 16, position: 'relative' }}>
      {/* Tooltip */}
      {hoverTip && (
        <div style={{
          position: 'fixed', left: hoverTip.x + 12, top: hoverTip.y - 8,
          background: C.navyMid, color: C.white, padding: '8px 12px', borderRadius: 8,
          fontSize: 11, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          pointerEvents: 'none', zIndex: 9999, maxWidth: 280, lineHeight: 1.5,
          border: `1px solid ${C.navyLight}`,
        }}>
          {hoverTip.text}
        </div>
      )}
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 14 }}>🚨 Risk & Remediation Summary</div>
      {appsWithFindings.map((app, i) => {
        const total = app.high + app.medium + app.low + app.risks.length;
        const pp = prePostMap[app.name];
        const resolved = pp ? pp.issues_fixed : 0;
        const resolvedPct = pp ? pp.issues_fixed_pct : 0;
        return (
          <div key={i} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{app.name}</span>
                <Badge label={app.decision} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 10, color: C.slate }}>{total} found</span>
                {resolved > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: C.green, background: `${C.green}12`, padding: '2px 7px', borderRadius: 4 }}>
                    ✅ {resolved} fixed ({resolvedPct}%)
                  </span>
                )}
              </div>
            </div>
            {/* Stacked bar */}
            <div style={{ display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden', background: C.border, width: '100%' }}>
              {app.high > 0 && (
                <div style={{ width: `${(app.high / maxFindings) * 100}%`, background: C.red, height: '100%', cursor: 'pointer' }}
                  onMouseEnter={e => setHoverTip({ x: e.clientX, y: e.clientY, text: `${app.high} HIGH severity — critical vulnerabilities requiring immediate attention` })}
                  onMouseMove={e => setHoverTip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                  onMouseLeave={() => setHoverTip(null)} />
              )}
              {app.medium > 0 && (
                <div style={{ width: `${(app.medium / maxFindings) * 100}%`, background: C.amber, height: '100%', cursor: 'pointer' }}
                  onMouseEnter={e => setHoverTip({ x: e.clientX, y: e.clientY, text: `${app.medium} MEDIUM severity — should be addressed before production` })}
                  onMouseMove={e => setHoverTip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                  onMouseLeave={() => setHoverTip(null)} />
              )}
              {app.low > 0 && (
                <div style={{ width: `${(app.low / maxFindings) * 100}%`, background: C.green, height: '100%', cursor: 'pointer' }}
                  onMouseEnter={e => setHoverTip({ x: e.clientX, y: e.clientY, text: `${app.low} LOW severity — minor issues, best-effort fixes` })}
                  onMouseMove={e => setHoverTip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                  onMouseLeave={() => setHoverTip(null)} />
              )}
              {app.risks.length > 0 && (
                <div style={{ width: `${(app.risks.length / maxFindings) * 100}%`, background: C.violet, height: '100%', cursor: 'pointer' }}
                  onMouseEnter={e => setHoverTip({ x: e.clientX, y: e.clientY, text: `${app.risks.length} migration risks identified by AI analysis` })}
                  onMouseMove={e => setHoverTip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                  onMouseLeave={() => setHoverTip(null)} />
              )}
            </div>
            {/* Resolution indicator bar */}
            {resolved > 0 && (
              <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ flex: 1, height: 8, background: C.border, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${resolvedPct}%`, height: '100%', background: C.green, borderRadius: 4 }} />
                </div>
                <span style={{ fontSize: 9, color: C.green, fontWeight: 600, whiteSpace: 'nowrap' }}>{resolvedPct}% resolved</span>
              </div>
            )}
            {/* Risk tags with tooltip */}
            {app.risks.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                {app.risks.slice(0, 3).map((risk, ri) => (
                  <span key={ri} style={{ fontSize: 9, color: C.textMid, background: `${C.violet}10`, border: `1px solid ${C.violet}20`, borderRadius: 4, padding: '2px 6px', cursor: 'pointer' }}
                    onMouseEnter={e => setHoverTip({ x: e.clientX, y: e.clientY, text: risk })}
                    onMouseMove={e => setHoverTip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                    onMouseLeave={() => setHoverTip(null)}>
                    {risk.length > 40 ? risk.slice(0, 38) + '…' : risk}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
        {[{label:'HIGH',color:C.red},{label:'MEDIUM',color:C.amber},{label:'LOW',color:C.green},{label:'Risks',color:C.violet}].map((l,i)=>(
          <div key={i} style={{ display:'flex', alignItems:'center', gap:4 }}>
            <div style={{ width:8, height:8, borderRadius:2, background:l.color }} />
            <span style={{ fontSize:9, color:C.textMid }}>{l.label}</span>
          </div>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 16, height: 4, borderRadius: 2, background: C.green }} />
          <span style={{ fontSize: 9, color: C.textMid }}>Resolved by AI</span>
        </div>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 1 — Portfolio Intelligence
// ═══════════════════════════════════════════════════════════════════════════════
function PortfolioSection({ stats }) {
  const { apps_by_decision = {}, lang_distribution = {}, complexity_distribution = {},
    all_apps = [], total_apps, total_loc, total_files } = stats;

  // Donut data for 6R
  const decisionDonut = Object.entries(apps_by_decision)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ label: k, value: v, color: DECISION_COLORS[k] || C.slate }));
  const totalDecisions = decisionDonut.reduce((s, d) => s + d.value, 0);

  // Language bar
  const langBar = Object.entries(lang_distribution)
    .sort((a, b) => b[1] - a[1])
    .map(([label, value], i) => ({
      label, value,
      color: [C.blue, C.cyan, C.violet, C.green, C.amber, C.red][i % 6]
    }));

  // Complexity per decision stacked bar
  const complexityKeys = ['Low', 'Medium', 'High', 'Very High'];
  const complexityColors = [C.green, C.amber, C.red, C.violet];
  const complexityByDecision = Object.keys(apps_by_decision).map(dec => {
    const appsForDec = all_apps.filter(a => a.decision === dec);
    const values = {};
    complexityKeys.forEach(k => {
      values[k] = appsForDec.filter(a => (a.migration_complexity || '').toLowerCase() === k.toLowerCase()).length;
    });
    return { label: dec, values };
  }).filter(d => Object.values(d.values).some(v => v > 0));

  // Unique languages
  const uniqueLangs = Object.keys(lang_distribution).length;
  // Avg risk score
  const avgRisk = all_apps.length
    ? (all_apps.reduce((s, a) => s + (a.llm_score || 0), 0) / all_apps.length).toFixed(1)
    : null;

  return (
    <div>
      <SectionHeader icon="🗂️" title="Application Portfolio Overview"
        subtitle="Technology landscape, migration strategy, and decision rationale" />

      {/* KPI Row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <KpiCard label="Total Apps" value={fmt(total_apps)} icon="📦" color={C.blue} sub="Applications analyzed by AI" />
        <KpiCard label="Avg Risk Score" value={avgRisk != null ? `${((avgRisk/10)*100).toFixed(0)}%` : '—'} sub="Higher = more complex to modernize" icon="⚠️" color={C.amber} />
        <KpiCard label="Unique Languages" value={fmt(uniqueLangs)} icon="💻" color={C.violet} sub="Technology diversity in portfolio" />
      </div>

      {/* Charts Row 1: Donut + Language Bar */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <DonutChart data={decisionDonut} size={240} thickness={40}
              label={fmt(totalDecisions)} sublabel="apps" />
            <div style={{ width: '100%' }}>
              {decisionDonut.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: d.color }} />
                    <span style={{ fontSize: 12, color: C.textMid }}>{d.label}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{d.value}</span>
                    <span style={{ fontSize: 10, color: C.slate, marginLeft: 4 }}>
                      ({totalDecisions > 0 ? ((d.value / totalDecisions) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>Technology Stack Breakdown</div>
          <div style={{ fontSize: 10, color: C.slate, marginBottom: 12 }}>Primary languages detected in legacy source applications</div>
          <HBarChart data={langBar} height={180} />
        </Card>
      </div>

      {/* Charts Row 2: Complexity stacked + App table */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, marginBottom: 16 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>Migration Effort Estimate</div>
          <div style={{ fontSize: 10, color: C.slate, marginBottom: 12 }}>Complexity level per migration strategy — higher = more effort to modernize</div>
          <StackedBarChart data={complexityByDecision} keys={complexityKeys} colors={complexityColors} height={160} />
        </Card>

        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Application Portfolio</div>
            <div style={{ fontSize: 10, color: C.slate, marginTop: 2 }}>Each legacy app with its AI-recommended migration path and risk level</div>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 260, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: C.bg, position: 'sticky', top: 0 }}>
                  {['App', 'Decision', 'Migration Risk', 'Complexity', 'Target Stack'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                      color: C.slate, textTransform: 'uppercase', letterSpacing: '0.04em',
                      borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {all_apps.map((app, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : C.bg }}>
                    <td style={{ padding: '7px 12px', fontWeight: 600, color: C.text, whiteSpace: 'nowrap', position: 'relative' }}>
                      <AppNameTip app={app} />
                    </td>
                    <td style={{ padding: '7px 12px' }}><Badge label={app.decision} /></td>
                    <td style={{ padding: '7px 12px', minWidth: 100 }}><RiskBar score={app.llm_score} reason={app.llm_reasoning} /></td>
                    <td style={{ padding: '7px 12px', color: C.textMid }}>{app.migration_complexity || '—'}</td>
                    <td style={{ padding: '7px 12px', color: C.textMid, whiteSpace: 'nowrap' }}>
                      {typeof app.target_stack === 'object' && app.target_stack !== null
                        ? `${app.target_stack.language || ''}${app.target_stack.framework ? ' / ' + app.target_stack.framework : ''}`
                        : app.target_stack || '—'}
                    </td>
                  </tr>
                ))}
                {all_apps.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: C.slate }}>No apps yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 2 — AI Spend & Execution Performance (real data only)
// ═══════════════════════════════════════════════════════════════════════════════
function CostSection({ stats }) {
  const {
    total_input_tokens = 0, total_output_tokens = 0, total_tokens = 0,
    actual_ai_cost_usd = 0, ai_cost_per_app = 0, total_kiro_calls = 0,
    token_by_agent = {}, stage_timing_avg = {}, total_duration_mins = 0,
    total_apps = 0, total_runs = 0, app_cost_breakdown = [],
    doc_hours_saved = 0, wiki_pages_total = 0,
  } = stats;

  const hasTokenData = total_tokens > 0;

  // Stage timing bar — real seconds
  // Requirements excluded: _timed_node captures ~0s due to HITL interrupt()
  const stageOrder = ['discovery','rationalization','coding','testing','security','iac','supervisor'];
  const stageColors = [C.blue, C.cyan, C.green, C.amber, C.red, C.slate, C.violet];
  const stageLabels = { discovery:'Discovery', rationalization:'Rationalization',
    coding:'Coding', testing:'Testing', security:'Security', iac:'IaC', supervisor:'Supervisor' };
  const stageBars = stageOrder
    .filter(s => stage_timing_avg[s] > 0)
    .map((s, i) => ({
      label: stageLabels[s] || s,
      value: Math.round(stage_timing_avg[s] || 0),
      color: stageColors[i],
    }));

  // Token by agent bar
  const tokenAgentBars = Object.entries(token_by_agent)
    .sort((a, b) => (b[1].input_tokens + b[1].output_tokens) - (a[1].input_tokens + a[1].output_tokens))
    .map(([name, v], i) => ({
      label: name,
      value: (v.input_tokens || 0) + (v.output_tokens || 0),
      color: [C.blue, C.violet, C.cyan, C.green, C.amber][i % 5],
    }));

  // Cost by agent bar
  const costAgentBars = Object.entries(token_by_agent)
    .sort((a, b) => {
      const costA = (a[1].input_tokens/1e6)*3 + (a[1].output_tokens/1e6)*15;
      const costB = (b[1].input_tokens/1e6)*3 + (b[1].output_tokens/1e6)*15;
      return costB - costA;
    })
    .map(([name, v], i) => {
      const cost = ((v.input_tokens||0)/1e6)*3 + ((v.output_tokens||0)/1e6)*15;
      return { label: name, value: parseFloat(cost.toFixed(4)), color: [C.violet, C.blue, C.cyan, C.green, C.amber][i % 5] };
    });

  // Per-app cost bar
  const appCostBars = app_cost_breakdown
    .filter(a => a.cost_usd > 0)
    .slice(0, 12)
    .map((a, i) => ({
      label: a.name,
      value: a.cost_usd,
      color: { Refactor: C.blue, New: C.green, Retire: C.red, Replace: C.amber }[a.decision] || C.slate,
    }));

  // Total pipeline time
  const totalStageSecs = Object.values(stage_timing_avg).reduce((s, v) => s + (v || 0), 0);
  const avgRunMins = total_duration_mins > 0
    ? (total_duration_mins / Math.max(total_runs, 1)).toFixed(1)
    : totalStageSecs > 0 ? (totalStageSecs / 60).toFixed(1) : null;

  return (
    <div>
      <SectionHeader icon="💸" title="AI Spend & Execution Performance"
        subtitle="Actual AI token usage, cost, and pipeline timing — all from real pipeline runs" />

      {/* KPI Row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <KpiCard label="Total AI Cost" value={hasTokenData ? fmtCost(actual_ai_cost_usd) : '—'}
          sub={hasTokenData ? `${fmt(total_kiro_calls)} kiro-cli calls` : 'Run pipeline to see'}
          icon="💰" color={C.green} />
        <KpiCard label="Cost per App" value={hasTokenData ? fmtCost(ai_cost_per_app) : '—'}
          sub={`${total_apps} apps assessed`} icon="📱" color={C.blue} />
        <KpiCard label="LLM Calls" value={fmt(total_kiro_calls)}
          sub="kiro-cli invocations" icon="🤖" color={C.violet} />
        <KpiCard label="Avg Pipeline Time" value={avgRunMins ? `${avgRunMins}m` : '—'}
          sub={`${total_runs} run${total_runs !== 1 ? 's' : ''}`} icon="⏱️" color={C.amber} />
        <KpiCard label="Input Tokens" value={hasTokenData ? `${(total_input_tokens/1000).toFixed(1)}K` : '—'}
          sub="$3/M (Claude Sonnet 4.6)" icon="📥" color={C.cyan} />
        <KpiCard label="Output Tokens" value={hasTokenData ? `${(total_output_tokens/1000).toFixed(1)}K` : '—'}
          sub="$15/M (Claude Sonnet 4.6)" icon="📤" color={C.red} />
      </div>

      {/* Pipeline output metrics row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Modules Generated',    value: fmt(stats.modules_generated || 0),         icon: '📦', color: C.blue,   sub: 'code modules created' },
          { label: 'Test Cases Written',   value: fmt(stats.total_test_cases_written || 0),   icon: '🧪', color: C.green,  sub: 'automated tests' },
          { label: 'Apps with Code',       value: fmt(stats.total_apps_with_code || 0),       icon: '💻', color: C.violet, sub: `of ${total_apps} apps` },
          { label: 'Wiki Pages Created',   value: fmt(stats.wiki_pages_total || 0),           icon: '📄', color: C.cyan,   sub: 'knowledge artifacts' },
          { label: 'Security Findings',    value: fmt((stats.total_security_high||0)+(stats.total_security_medium||0)+(stats.total_security_low||0)), icon: '🔍', color: C.amber, sub: `${stats.total_security_high||0} HIGH` },
        ].map((item, i) => (
          <KpiCard key={i} label={item.label} value={item.value} sub={item.sub} icon={item.icon} color={item.color} />
        ))}
      </div>

      {/* Token pricing note */}
      {!hasTokenData && (
        <div style={{ background: `${C.amber}10`, border: `1px solid ${C.amber}30`,
          borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 12, color: C.textMid }}>
          ⚠️ Token data requires a fresh pipeline run. Rerun the pipeline to capture actual AI spend.
        </div>
      )}
      {hasTokenData && total_kiro_calls > 0 && actual_ai_cost_usd > 0 && (
        <div style={{ background: `${C.blue}08`, border: `1px solid ${C.blue}20`,
          borderRadius: 8, padding: '8px 16px', marginBottom: 16, fontSize: 11, color: C.textMid }}>
          ℹ️ Token counts are <strong>estimated</strong> from agent execution time (200 out tok/s · 800 in tok/s).
          Rerun the pipeline to capture exact token counts.
        </div>
      )}

      {/* Charts Row 1: Stage timing + Token by agent */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>Stage Execution Time (seconds)</div>
          <div style={{ fontSize: 11, color: C.slate, marginBottom: 12 }}>Real wall-clock time per pipeline stage</div>
          {stageBars.length > 0
            ? <HBarChart data={stageBars} height={200} format={v => `${v}s`} />
            : <div style={{ color: C.slate, fontSize: 12, padding: 20 }}>No timing data yet</div>}
        </Card>

        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>Token Usage by Agent</div>
          <div style={{ fontSize: 11, color: C.slate, marginBottom: 12 }}>Total tokens (input + output) per agent</div>
          {tokenAgentBars.length > 0
            ? <HBarChart data={tokenAgentBars} height={200} format={v => `${(v/1000).toFixed(1)}K`} />
            : <div style={{ color: C.slate, fontSize: 12, padding: 20 }}>Run pipeline to see token usage</div>}
        </Card>
      </div>

      {/* Charts Row 2: Cost by agent — full width */}
      <div style={{ marginBottom: 16 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>AI Cost by Agent ($)</div>
          <div style={{ fontSize: 11, color: C.slate, marginBottom: 12 }}>
            Input: $3/M · Output: $15/M (Claude Sonnet 4.6)
          </div>
          {costAgentBars.length > 0
            ? <HBarChart data={costAgentBars} height={200} format={v => fmtCost(v)} />
            : <div style={{ color: C.slate, fontSize: 12, padding: 20 }}>Run pipeline to see cost breakdown</div>}
        </Card>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 3 — Quality & Confidence
// ═══════════════════════════════════════════════════════════════════════════════
function QualitySection({ stats, runs }) {
  const {
    avg_coverage, total_security_high, total_security_medium, total_security_low,
    acceptance_rate_avg, quality_gate_summary = {},
    waf_avg = {}, effort_distribution = {},
  } = stats;

  const securityScore = Math.max(0, 100 - (total_security_high || 0) * 8 - (total_security_medium || 0) * 2);
  const wafComposite = waf_avg && Object.keys(waf_avg).length
    ? (Object.values(waf_avg).reduce((s, v) => s + (v || 0), 0) / Object.keys(waf_avg).length) * 20
    : null;

  const secDonut = [
    { label: 'High',   value: total_security_high   || 0, color: C.red   },
    { label: 'Medium', value: total_security_medium || 0, color: C.amber },
    { label: 'Low',    value: total_security_low    || 0, color: C.green },
  ].filter(d => d.value > 0);

  const effortDonut = Object.entries(effort_distribution)
    .filter(([, v]) => v > 0)
    .map(([k, v], i) => ({ label: k, value: v, color: [C.green, C.amber, C.red][i % 3] }));

  const wafKeys = ['security','reliability','cost_optimization','operational_excellence','performance_efficiency'];
  const wafLabels = { security:'Security', reliability:'Reliability', cost_optimization:'Cost Opt.',
    operational_excellence:'Ops Excellence', performance_efficiency:'Performance' };
  const wafBars = wafKeys.map(k => ({
    label: wafLabels[k] || k,
    value: waf_avg[k] != null ? waf_avg[k] : 0,
    color: C.blue,
  }));

  const qgTotal = quality_gate_summary.total || 0;
  const qgPassRate = qgTotal > 0 ? ((quality_gate_summary.passed || 0) / qgTotal * 100) : null;

  return (
    <div>
      <SectionHeader icon="🛡️" title="Modernization Quality Assurance"
        subtitle="Code quality, security posture, and modernization impact" />

      {/* Compact KPI + Gauges combined */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <GaugeChart value={avg_coverage} color={C.green} label="Test Coverage" size={140} />
            <div style={{ fontSize: 10, color: C.textMid, marginTop: 4 }}>Target: 90%+</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <GaugeChart value={securityScore} color={securityScore >= 80 ? C.green : securityScore >= 50 ? C.amber : C.red} label="Security Score" size={120} />
            <div style={{ fontSize: 10, color: C.textMid, marginTop: 4 }}>{fmt(total_security_high)} HIGH · {fmt(total_security_medium)} MED</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <GaugeChart value={acceptance_rate_avg} color={C.blue} label="AI Acceptance" size={140} />
            <div style={{ fontSize: 10, color: C.textMid, marginTop: 4 }}>Human-approved decisions</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <GaugeChart value={qgPassRate} color={C.violet} label="Quality Gate" size={140} />
            <div style={{ fontSize: 10, color: C.textMid, marginTop: 4 }}>{quality_gate_summary.passed || 0}/{qgTotal} passed first try</div>
          </div>
        </div>
      </Card>

      {/* WAF + Security in one row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16, overflow: 'visible' }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>Well-Architected Framework Scores</div>
          <div style={{ fontSize: 10, color: C.slate, marginBottom: 12 }}>Derived from security scans, test coverage, complexity, and retry rates</div>
          <HBarChart data={wafBars} height={150} format={v => `${Math.round(v)}%`} />
        </Card>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Security Findings</div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'visible' }}>
            <DonutChart data={secDonut} size={180} thickness={34}
              label={fmt((total_security_high||0)+(total_security_medium||0)+(total_security_low||0))} sublabel="total" />
          </div>
          <div style={{ marginTop: 12 }}>
            {[{label:'High',value:total_security_high,color:C.red},{label:'Medium',value:total_security_medium,color:C.amber},{label:'Low',value:total_security_low,color:C.green}].map((s,i)=>(
              <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <div style={{ width:8, height:8, borderRadius:2, background:s.color }} />
                  <span style={{ fontSize:11, color:C.textMid }}>{s.label}</span>
                </div>
                <span style={{ fontSize:12, fontWeight:700, color:s.color }}>{fmt(s.value)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Critical Findings per App — Stacked Bar Chart */}
      {(stats.all_apps || []).some(a => (a.llm_risks || []).length > 0 || a.security_high > 0 || a.security_medium > 0) && (() => {
        const appsWithFindings = (stats.all_apps || [])
          .map(a => ({
            name: a.name,
            decision: a.decision,
            high: a.security_high || 0,
            medium: a.security_medium || 0,
            low: a.security_low || 0,
            risks: a.llm_risks || [],
            total: (a.security_high || 0) * 3 + (a.security_medium || 0) * 2 + (a.llm_risks || []).length,
          }))
          .filter(a => a.high > 0 || a.medium > 0 || a.risks.length > 0)
          .sort((a, b) => b.total - a.total);

        if (!appsWithFindings.length) return null;

        const maxFindings = Math.max(...appsWithFindings.map(a => a.high + a.medium + a.low + a.risks.length), 1);

        return (
          <CriticalFindingsCard appsWithFindings={appsWithFindings} maxFindings={maxFindings} prePost={stats.pre_post_comparison || []} />
        );
      })()}

      {/* Pre vs Post Modernization — Visual Comparison */}
      {(stats.pre_post_comparison || []).length > 0 && (() => {
        const apps = stats.pre_post_comparison || [];
        const totalIssuesFixed = apps.reduce((s, a) => s + (a.issues_fixed || 0), 0);
        const totalPreIssues = apps.reduce((s, a) => s + (a.pre_issues || 0), 0);
        const avgCovGain = apps.length ? apps.reduce((s, a) => s + (a.coverage_gain || 0), 0) / apps.length : 0;
        const totalTests = apps.reduce((s, a) => s + (a.post_test_count || 0), 0);
        const eolFixed = apps.filter(a => a.pre_eol && !a.post_eol).length;
        const eolTotal = apps.filter(a => a.pre_eol).length;
        const issuesFixedPct = totalPreIssues > 0 ? Math.round((totalIssuesFixed / totalPreIssues) * 100) : 0;
        const frameworkScore = eolTotal > 0 ? Math.round((eolFixed / eolTotal) * 100) : 100;
        // Composite modernization score
        const modScore = Math.round((issuesFixedPct + avgCovGain + frameworkScore) / 3);

        return (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>🔄</span>
              <span>Modernization Impact Analysis</span>
              <span style={{ fontSize: 10, color: C.slate, fontWeight: 400 }}>— Before & after comparison per application</span>
            </div>

            {/* Row 1: Improvement Gauges */}
            <Card style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 14 }}>Modernization Impact Scores</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                <div style={{ textAlign: 'center' }}>
                  <GaugeChart value={issuesFixedPct} color={C.green} label="Issues Resolved" size={140} />
                  <div style={{ fontSize: 10, color: C.textMid, marginTop: 4 }}>{totalIssuesFixed} of {totalPreIssues} fixed</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <GaugeChart value={avgCovGain} color={C.blue} label="Coverage Gained" size={140} />
                  <div style={{ fontSize: 10, color: C.textMid, marginTop: 4 }}>0% → {avgCovGain.toFixed(1)}%</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <GaugeChart value={frameworkScore} color={C.violet} label="EOL Upgraded" size={140} />
                  <div style={{ fontSize: 10, color: C.textMid, marginTop: 4 }}>{eolFixed} of {eolTotal || apps.length} frameworks</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <GaugeChart value={modScore} color={C.cyan} label="Overall Score" size={140} />
                  <div style={{ fontSize: 10, color: C.textMid, marginTop: 4 }}>Composite improvement</div>
                </div>
              </div>
            </Card>

            {/* Per-App Transformation Cards — Collapsible */}
            <CollapsibleAppCards apps={apps} />

            {/* Summary Banner */}
            <div style={{ background: `linear-gradient(135deg, ${C.green}12, ${C.blue}08)`, border: `1px solid ${C.green}30`, borderRadius: 12, padding: '14px 20px', display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 12 }}>
              {[
                { icon: '🛡️', value: `${issuesFixedPct}%`, label: 'Issues Resolved' },
                { icon: '✅', value: `+${avgCovGain.toFixed(1)}%`, label: 'Coverage Gained' },
                { icon: '🧪', value: fmt(totalTests), label: 'Tests Written' },
                { icon: '🔄', value: `${eolFixed}/${eolTotal || apps.length}`, label: 'EOL Upgraded' },
                { icon: '⚡', value: `${modScore}%`, label: 'Modernization Score' },
              ].map((item, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 16 }}>{item.icon}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.green }}>{item.value}</div>
                  <div style={{ fontSize: 10, color: C.textMid }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
function AIEngineSection({ stats }) {
  const {
    total_runs, total_duration_mins, agent_stats_agg = {},
    wiki_pages_total, wiki_pages_by_type = {}, wiki_pages_by_app = {},
    wiki_cross_refs, stage_timing_avg = {}, total_apps, top_risks = [],
  } = stats;

  const agentEntries = Object.entries(agent_stats_agg);
  const avgSuccessRate = agentEntries.length
    ? agentEntries.reduce((s,[,v])=>s+(v.success_rate||0),0)/agentEntries.length*100 : null;

  const stageOrder = ['discovery','rationalization','requirements','coding','testing','security','iac','supervisor'];
  const stageColors = [C.blue,C.cyan,C.violet,C.green,C.amber,C.red,C.slate,'#6366F1'];
  const stageBars = stageOrder.filter(s=>stage_timing_avg[s]>0).map((s,i)=>({
    label: s.charAt(0).toUpperCase()+s.slice(1),
    value: Math.round(stage_timing_avg[s]||0),
    color: stageColors[i],
  }));

  const wikiTypeDonut = Object.entries(wiki_pages_by_type).filter(([,v])=>v>0)
    .map(([k,v],i)=>({ label:k.charAt(0).toUpperCase()+k.slice(1), value:v, color:[C.blue,C.violet,C.cyan][i%3] }));

  const wikiAppBar = Object.entries(wiki_pages_by_app).sort((a,b)=>b[1]-a[1]).slice(0,10)
    .map(([label,value],i)=>({ label, value, color:[C.blue,C.cyan,C.violet,C.green,C.amber][i%5] }));

  return (
    <div>
      <SectionHeader icon="🤖" title="AI Engine"
        subtitle="Pipeline performance, agent metrics, and knowledge base" />
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <KpiCard label="Pipeline Runs"  value={fmt(total_runs)} sub="Total modernization cycles completed" icon="🔄" color={C.blue} />
        <KpiCard label="Avg Duration"   value={total_duration_mins>0?`${(total_duration_mins/Math.max(total_runs,1)).toFixed(1)}m`:'—'} sub="Average time per pipeline run" icon="⏱️" color={C.cyan} />
        <KpiCard label="Agent Success"  value={avgSuccessRate!=null?pct(avgSuccessRate):'—'} sub="% of AI agent calls completed without error" icon="✅" color={C.green} />
        <KpiCard label="Wiki Pages"     value={fmt(wiki_pages_total)} sub="Knowledge artifacts auto-generated" icon="📚" color={C.violet} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Stage Timing (avg seconds)</div>
          {stageBars.length>0 ? <HBarChart data={stageBars} height={220} format={v=>`${v}s`} />
            : <div style={{ color:C.slate, fontSize:12, padding:20 }}>No timing data yet</div>}
        </Card>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Wiki Pages by Type</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <DonutChart data={wikiTypeDonut} size={130} thickness={28} label={fmt(wiki_pages_total)} sublabel="pages" />
            <div style={{ flex: 1 }}>
              {wikiTypeDonut.map((d,i)=>(
                <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <div style={{ width:8, height:8, borderRadius:2, background:d.color }} />
                    <span style={{ fontSize:11, color:C.textMid }}>{d.label}</span>
                  </div>
                  <span style={{ fontSize:12, fontWeight:700, color:C.text }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
          {wikiAppBar.length>0 && (
            <div style={{ marginTop:16 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.slate, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:8 }}>Pages by App</div>
              <HBarChart data={wikiAppBar} height={140} />
            </div>
          )}
        </Card>
      </div>
      <Card style={{ padding:0, overflow:'hidden' }}>
        <div style={{ padding:'14px 16px 10px', fontSize:13, fontWeight:700, color:C.text, borderBottom:`1px solid ${C.border}` }}>Agent Performance</div>
        <div style={{ overflowY:'auto', maxHeight:320 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ background:C.bg, position:'sticky', top:0 }}>
                {['Agent','Calls','Avg ms','Success'].map(h=>(
                  <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:10, fontWeight:700, color:C.slate, textTransform:'uppercase', letterSpacing:'0.04em', borderBottom:`1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agentEntries.sort((a,b)=>(b[1].calls||0)-(a[1].calls||0)).map(([name,v],i)=>{
                const sr=(v.success_rate||0)*100;
                const srColor=sr>=90?C.green:sr>=70?C.amber:C.red;
                return (
                  <tr key={i} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?C.white:C.bg }}>
                    <td style={{ padding:'7px 12px', fontWeight:600, color:C.text }}>{name}</td>
                    <td style={{ padding:'7px 12px', color:C.textMid }}>{fmt(v.calls)}</td>
                    <td style={{ padding:'7px 12px', color:C.textMid }}>{v.avg_ms!=null?fmt(v.avg_ms):'—'}</td>
                    <td style={{ padding:'7px 12px' }}><span style={{ fontWeight:700, color:srColor }}>{pct(sr)}</span></td>
                  </tr>
                );
              })}
              {agentEntries.length===0&&(<tr><td colSpan={4} style={{ padding:20, textAlign:'center', color:C.slate }}>No agent data yet</td></tr>)}
            </tbody>
          </table>
        </div>
      </Card>
      {top_risks.length>0&&(
        <Card style={{ marginTop:16 }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:12 }}>Top Risks Identified</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {top_risks.slice(0,12).map((r,i)=>(
              <div key={i} style={{ display:'flex', alignItems:'center', gap:6, background:`${C.red}10`, border:`1px solid ${C.red}25`, borderRadius:8, padding:'5px 10px' }}>
                <span style={{ fontSize:11, color:C.textMid }}>{r.risk}</span>
                <span style={{ fontSize:11, fontWeight:700, color:C.red, background:`${C.red}20`, borderRadius:99, padding:'1px 6px' }}>{r.count}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROJECT COMPARISON SECTION
// ═══════════════════════════════════════════════════════════════════════════════
/**
 * ProjectComparisonSection — side-by-side metrics for up to 20 projects.
 * Only renders when isOrgLevel is true (org-level view, no specific project selected).
 * Hides itself if there is only 1 project (or 0).
 *
 * Requirements: 15.3, 15.4
 */
function ProjectComparisonSection({ orgId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    apiFetch(`/api/dashboard/compare?org_id=${orgId}`)
      .then(res => {
        if (!res.ok) throw new Error(`Compare API error: ${res.status}`);
        return res.json();
      })
      .then(json => {
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [orgId]);

  // Hide if loading, error, no data, or only 1 project (Requirement 15.4)
  if (loading || error || !data || !data.projects || data.projects.length <= 1) {
    return null;
  }

  const projects = data.projects;

  const metrics = [
    { key: 'total_apps', label: 'Applications', icon: '📦', format: v => fmt(v) },
    { key: 'total_security_high', label: 'Security (High)', icon: '🛡️', format: v => fmt(v) },
    { key: 'total_cost_usd', label: 'Estimated Cost', icon: '💰', format: v => fmtUSD(v) },
    { key: 'avg_coverage', label: 'Test Coverage', icon: '🧪', format: v => pct(v) },
    { key: 'total_runs', label: 'Pipeline Runs', icon: '🔄', format: v => fmt(v) },
  ];

  const cellStyle = {
    padding: '10px 14px',
    fontSize: 13,
    borderBottom: `1px solid ${C.border}`,
    whiteSpace: 'nowrap',
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 700,
    fontSize: 12,
    color: C.slate,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    background: C.bg,
    position: 'sticky',
    top: 0,
  };

  return (
    <div style={{ marginBottom: 28 }}>
      <SectionHeader
        icon="📊"
        title="Project Comparison"
        subtitle={`Side-by-side metrics for ${projects.length} projects in this organisation`}
      />
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: projects.length > 4 ? projects.length * 140 : 'auto' }}>
            <thead>
              <tr>
                <th style={{ ...headerCellStyle, textAlign: 'left', position: 'sticky', left: 0, background: C.bg, zIndex: 2 }}>
                  Metric
                </th>
                {projects.map(p => (
                  <th key={p.project_id} style={{ ...headerCellStyle, textAlign: 'right' }}>
                    {p.project_name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric, idx) => (
                <tr key={metric.key} style={{ background: idx % 2 === 0 ? C.white : C.bg }}>
                  <td style={{ ...cellStyle, fontWeight: 600, color: C.text, position: 'sticky', left: 0, background: idx % 2 === 0 ? C.white : C.bg, zIndex: 1 }}>
                    <span style={{ marginRight: 8 }}>{metric.icon}</span>
                    {metric.label}
                  </td>
                  {projects.map(p => {
                    const value = (p.stats || {})[metric.key] || 0;
                    return (
                      <td key={p.project_id} style={{ ...cellStyle, textAlign: 'right', fontWeight: 600, color: C.text }}>
                        {metric.format(value)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
// ─── Scope Banner ─────────────────────────────────────────────────────────────
function ScopeBanner({ orgName, projectName, isOrgLevel }) {
  if (!orgName) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20,
      padding: '10px 16px', borderRadius: 10,
      background: isOrgLevel ? `${C.violet}08` : `${C.blue}08`,
      border: `1px solid ${isOrgLevel ? C.violet + '25' : C.blue + '25'}`,
    }}>
      <span style={{ fontSize: 18 }}>{isOrgLevel ? '🏢' : '📁'}</span>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
          {isOrgLevel ? `${orgName} — All Projects` : `${orgName} / ${projectName}`}
        </div>
        <div style={{ fontSize: 11, color: C.textMid }}>
          {isOrgLevel
            ? 'Showing aggregated statistics across all projects in this organisation'
            : 'Showing statistics filtered to this project'}
        </div>
      </div>
      {isOrgLevel && (
        <span style={{
          marginLeft: 'auto', fontSize: 10, fontWeight: 600,
          color: C.violet, background: `${C.violet}15`,
          padding: '3px 10px', borderRadius: 99,
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          Org Aggregate
        </span>
      )}
      {!isOrgLevel && projectName && (
        <span style={{
          marginLeft: 'auto', fontSize: 10, fontWeight: 600,
          color: C.blue, background: `${C.blue}15`,
          padding: '3px 10px', borderRadius: 99,
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          Project View
        </span>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { orgId, projectId, orgName, projectName, contextVersion, loading: contextLoading } = useOrgProject();
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const prevContextVersionRef = useRef(contextVersion);
  // Track whether we're switching context (vs initial load) for UX differentiation
  const [isContextSwitching, setIsContextSwitching] = useState(false);
  const contextSwitchStartRef = useRef(null);

  // Determine if we're viewing org-level aggregates (no specific project selected)
  const isOrgLevel = Boolean(orgId && !projectId);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      // Build query params with org/project scope
      const params = new URLSearchParams();
      if (orgId) params.set('org_id', orgId);
      if (projectId) params.set('project_id', projectId);
      const qs = params.toString() ? `?${params.toString()}` : '';

      const [statsRes, runsRes] = await Promise.all([
        apiFetch(`/api/dashboard/stats${qs}`),
        apiFetch(`/api/dashboard/runs${qs}`),
      ]);
      if (!statsRes.ok) throw new Error(`Stats API error: ${statsRes.status}`);
      const statsData = await statsRes.json();
      setStats(statsData);
      if (runsRes.ok) {
        const runsData = await runsRes.json();
        setRuns(Array.isArray(runsData) ? runsData : runsData.runs || []);
      }
      setLastRefresh(new Date());

      // Log context switch performance (Requirement 15.5: refresh within 2 seconds)
      if (contextSwitchStartRef.current) {
        const elapsed = Date.now() - contextSwitchStartRef.current;
        if (process.env.NODE_ENV === 'development' && elapsed > 2000) {
          console.warn(`[Dashboard] Context switch took ${elapsed}ms (target: <2000ms)`);
        }
        contextSwitchStartRef.current = null;
      }
    } catch (e) {
      setError(e.message);
      contextSwitchStartRef.current = null;
    } finally {
      setLoading(false);
      setIsContextSwitching(false);
    }
  }, [orgId, projectId]);

  // Show loading indicator when context changes (org/project switch)
  // Requirement 15.5: display a loading indicator and refresh within 2 seconds
  useEffect(() => {
    if (prevContextVersionRef.current !== contextVersion) {
      prevContextVersionRef.current = contextVersion;
      setLoading(true);
      setIsContextSwitching(true);
      contextSwitchStartRef.current = Date.now();
    }
  }, [contextVersion]);

  useEffect(() => {
    // Don't fetch until context is initialized
    if (contextLoading) return;
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData, contextLoading]);

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ padding:'24px 32px' }}>
        {loading&&(
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:400, gap:12 }}>
            <div style={{ width:32, height:32, border:`3px solid ${C.border}`, borderTopColor:C.blue, borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
            <span style={{ color:C.textMid, fontSize:15 }}>
              {isContextSwitching ? 'Switching context…' : 'Loading dashboard…'}
            </span>
            {isContextSwitching && (
              <span style={{ color:C.slate, fontSize:12 }}>
                Loading data for {orgName || 'organisation'}{projectName ? ` / ${projectName}` : ''}
              </span>
            )}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        {error&&!loading&&(
          <div style={{ background:`${C.red}10`, border:`1px solid ${C.red}30`, borderRadius:12, padding:'20px 24px', marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight:700, color:C.red, marginBottom:2 }}>Failed to load dashboard</div>
              <div style={{ fontSize:13, color:C.textMid }}>{error}</div>
            </div>
            <button onClick={fetchData} style={{ marginLeft:'auto', background:C.red, color:C.white, border:'none', borderRadius:8, padding:'8px 16px', fontSize:12, fontWeight:600, cursor:'pointer' }}>Retry</button>
          </div>
        )}
        {!loading&&stats&&(
          stats.total_runs===0 ? <EmptyState orgName={orgName} projectName={projectName} isOrgLevel={isOrgLevel} /> : (
            <>
              {/* Scope indicator: shows whether viewing org-level aggregates or project-specific data */}
              <ScopeBanner orgName={orgName} projectName={projectName} isOrgLevel={isOrgLevel} />

              {/* Org-level aggregate KPIs: total applications, pipeline runs, security findings, cost estimates */}
              {isOrgLevel && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                  <KpiCard label="Total Applications" value={fmt(stats.total_apps)} icon="📦" color={C.blue}
                    sub="Across all projects" />
                  <KpiCard label="Total Pipeline Runs" value={fmt(stats.total_runs)} icon="🔄" color={C.cyan}
                    sub="All project runs combined" />
                  <KpiCard label="Security Findings" value={fmt((stats.total_security_high || 0) + (stats.total_security_medium || 0) + (stats.total_security_low || 0))} icon="🛡️" color={C.red}
                    sub={`${fmt(stats.total_security_high)} high · ${fmt(stats.total_security_medium)} med · ${fmt(stats.total_security_low)} low`} />
                  <KpiCard label="Total Cost Estimate" value={fmtUSD(stats.total_cost_usd || stats.baseline_cost || 0)} icon="💰" color={C.amber}
                    sub="Estimated modernisation cost" />
                </div>
              )}

              {/* Project comparison: side-by-side metrics for all projects in the org (Req 15.3, 15.4) */}
              {isOrgLevel && <ProjectComparisonSection orgId={orgId} />}

              {/* Project-level drill-down KPIs: same metric categories filtered to selected project */}
              {!isOrgLevel && projectId && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                  <KpiCard label="Applications" value={fmt(stats.total_apps)} icon="📦" color={C.blue}
                    sub={`In ${projectName || 'this project'}`} />
                  <KpiCard label="Pipeline Runs" value={fmt(stats.total_runs)} icon="🔄" color={C.cyan}
                    sub="Project pipeline runs" />
                  <KpiCard label="Security Findings" value={fmt((stats.total_security_high || 0) + (stats.total_security_medium || 0) + (stats.total_security_low || 0))} icon="🛡️" color={C.red}
                    sub={`${fmt(stats.total_security_high)} high · ${fmt(stats.total_security_medium)} med · ${fmt(stats.total_security_low)} low`} />
                  <KpiCard label="Cost Estimate" value={fmtUSD(stats.total_cost_usd || stats.baseline_cost || 0)} icon="💰" color={C.amber}
                    sub="Project modernisation cost" />
                </div>
              )}

              <div style={{ display:'flex', gap:0, marginBottom:24, background:C.navy, borderRadius:12, overflow:'hidden', boxShadow:'0 4px 12px rgba(15,23,42,0.2)' }}>
                {[
                  { label:'Pipeline Runs', value:fmt(stats.total_runs), color:C.blue },
                  { label:'Apps Assessed', value:fmt(stats.total_apps), color:C.cyan },
                  { label:'Test Coverage', value:pct(stats.avg_coverage), color:C.green },
                  { label:'Sec HIGH',      value:fmt(stats.total_security_high), color:C.red },
                  { label:'AI Acceptance', value:pct(stats.acceptance_rate_avg), color:C.blue },
                ].map((item,i,arr)=>(
                  <div key={i} style={{ flex:1, padding:'14px 16px', textAlign:'center', borderRight:i<arr.length-1?`1px solid ${C.navyLight}`:'none' }}>
                    <div style={{ fontSize:10, color:C.slateLight, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>{item.label}</div>
                    <div style={{ fontSize:18, fontWeight:800, color:item.color }}>{item.value}</div>
                  </div>
                ))}
              </div>
              <PortfolioSection stats={stats} />
              <div style={{ height:1, background:C.border, margin:'32px 0' }} />
              <QualitySection stats={stats} runs={runs} />
            </>
          )
        )}
      </div>
    </div>
  );
}
